<template>
  <div class="pt-2">
    <!-- Products Table -->
    <div class="overflow-x-auto overflow-y-auto">
      <v-data-table
        :headers="headers"
        :items="products"
        :search="search"
        :loading="loading"
        class="table-radius border border-[#e0e0e0]"
        fixed-header
      >
        <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
          <p
            :key="'prod-tt-' + index"
            class="font-title"
            v-if="header.text?.length > 15"
            v-tooltip="{ text: header.text }"
          >
            {{ header.text }}
          </p>
          <p :key="'prod-txt-' + index" v-else class="font-title">{{ header.text }}</p>
        </template>

        <!-- Row Data Styling -->
        <template v-slot:item.productName="{ item }">
          <td class="px-4 py-3 table-font">{{ item.productName }}</td>
        </template>
        <template v-slot:item.productCode="{ item }">
          <td class="px-4 table-font">{{ item.productCode }}</td>
        </template>
        <template v-slot:item.action="{ item }">
          <td class="pl-4">
            <ActionButton
              :item="item"
              :canEdit="true"
              :canDelete="true"
              :handleEdit="() => openDialog(item)"
              :handleDelete="() => deleteItem(item)"
              :showEditButton="true"
              :showDeleteButton="true"
            />
          </td>
        </template>
      </v-data-table>
    </div>

    <!-- Dialog for Add/Edit -->
    <v-dialog v-model="dialog" max-width="500px" persistent>
      <v-card class="rounded-lg overflow-hidden p-2">
        <v-card-title class="bg-gray-50 border-b border-gray-200 px-6 py-4">
          <span class="text-lg font-bold text-[#21427d]">{{
            isEdit ? 'Edit Product' : 'Add Product'
          }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog" small><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text class="px-6 py-8">
          <v-form ref="form" v-model="valid">
            <v-row dense>
              <v-col cols="12">
                <p class="text-sm font-semibold text-gray-700 mb-1">Product Name</p>
                <v-text-field
                  v-model="editedItem.productName"
                  placeholder="Enter Product Name"
                  outlined
                  dense
                  hide-details="auto"
                  class="mb-2"
                  :rules="[(v) => !!v || 'Product Name is required']"
                ></v-text-field>
              </v-col>

              <v-col cols="12">
                <p class="text-sm font-semibold text-gray-700 mb-1">Product Code</p>
                <v-text-field
                  v-model="editedItem.productCode"
                  placeholder="Enter Product Code"
                  outlined
                  dense
                  hide-details="auto"
                  class="mb-2"
                  :rules="[(v) => !!v || 'Product Code is required']"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 py-4 border-t border-gray-100 bg-gray-50">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeDialog" class="text-capitalize"
            >Cancel</v-btn
          >
          <v-btn
            color="primary"
            class="px-8 white--text text-capitalize elevation-0"
            :disabled="!valid"
            @click="save"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ActionButton from '@/components/ActionButton.vue'

export default {
  name: 'ProductsTab',
  components: {
    ActionButton
  },
  props: {
    search: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      dialog: false,
      valid: false,
      loading: false,
      headers: [
        { text: 'Product Name', value: 'productName', sortable: false },
        { text: 'Product Code', value: 'productCode', sortable: false },
        { text: 'Actions', value: 'action', sortable: false }
      ],
      products: [],
      editedItem: { productName: '', productCode: '' },
      defaultItem: { productName: '', productCode: '' },
      isEdit: false
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const res = await this.$axios.get('/inventory/products')

        // Handle varying possible fastify API structures
        const fetchedProducts = res.data?.products
        this.products = Array.isArray(fetchedProducts) ? fetchedProducts : []
      } catch (e) {
        console.error('Error fetching products', e)
      } finally {
        this.loading = false
      }
    },
    openDialog(item) {
      if (item) {
        this.isEdit = true
        this.editedItem = Object.assign({}, item)
      } else {
        this.isEdit = false
        this.editedItem = Object.assign({}, this.defaultItem)
      }
      this.dialog = true
      if (this.$refs.form) this.$refs.form.resetValidation()
    },
    closeDialog() {
      this.dialog = false
    },
    async save() {
      if (this.$refs.form.validate()) {
        try {
          if (this.isEdit) {
            await this.$axios.put(`/inventory/products/${this.editedItem._id}`, this.editedItem)
            this.$toast.success('Product updated')
          } else {
            await this.$axios.post('/inventory/products', this.editedItem)
            this.$toast.success('Product created')
          }
          this.closeDialog()
          this.fetchData()
        } catch (e) {
          const msg = e.response?.data?.message || 'Failed to save product'
          this.$toast.error(msg)
        }
      }
    },
    async deleteItem(item) {
      if (confirm('Are you sure you want to delete this product?')) {
        try {
          await this.$axios.delete(`/inventory/products/${item._id}`)
          this.$toast.success('Product deleted')
          this.fetchData()
        } catch (e) {
          const msg = e.response?.data?.message || 'Failed to delete product'
          this.$toast.error(msg)
        }
      }
    }
  }
}
</script>

<style scoped>
.table-radius {
  border-radius: 0px !important;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
